import {
  Box,
  type BoxProps,
  Circle,
  createIcon,
  Icon,
  Stack,
  type StackProps,
  useId,
  useRadio,
  useRadioGroup,
  type UseRadioProps,
  useStyleConfig,
} from '@chakra-ui/react';
import { Children, cloneElement, isValidElement, ReactElement } from 'react';

interface SingleChoiceAnswerProps<T> extends Omit<StackProps, 'onChange'> {
  name?: string;
  value?: T;
  defaultValue?: string;
  onChange?: (value: T) => void;
  children?: ReactElement<SingleChoiceAnswerCardProps>[];
}

interface SingleChoiceAnswerCardProps extends BoxProps {
  value: string;
  radioProps?: UseRadioProps;
  readOnly?: boolean;
  isCorrect?: boolean;
  selectedOptions?: string[];
  index?: number;
}

const CheckIcon = createIcon({
  displayName: 'CheckIcon',
  viewBox: '0 0 12 10',
  path: (
    <polyline
      fill="none"
      strokeWidth="2px"
      stroke="currentColor"
      strokeDasharray="16px"
      points="1.5 6 4.5 9 10.5 1"
    />
  ),
});

export function SingleChoiceAnswer<T extends string>(props: SingleChoiceAnswerProps<T>) {
  const { children, name, defaultValue, value, onChange } = props;
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    defaultValue,
    value,
    onChange,
  });

  const cards = Children.toArray(children)
    .map((element, index) => {
      if (isValidElement<SingleChoiceAnswerCardProps>(element)) {
        const props = element.props;
        if (
          typeof props === 'object' &&
          props !== null &&
          'value' in props &&
          typeof props.value === 'string'
        ) {
          return cloneElement<SingleChoiceAnswerCardProps>(element, {
            radioProps: getRadioProps({ value: props.value }),
            autoFocus: !props.value && index === 0,
            key: props.value,
          });
        }
      }
      // SingleChoiceAnswer card missing required value prop
      return null;
    })
    .filter(isValidElement);

  return (
    <Stack mt="16px" {...getRootProps()}>
      {cards}
    </Stack>
  );
}

export function SingleChoiceAnswerCard(props: SingleChoiceAnswerCardProps) {
  const {
    radioProps,
    children,
    readOnly = false,
    isCorrect: _isCorrect,
    selectedOptions: _selectedOptions,
    autoFocus,
    ...rest
  } = props;
  const { getInputProps, getRadioProps, state } = useRadio(radioProps);
  const id = useId(undefined, 'radio-button');

  const styles = useStyleConfig('RadioCard', props);
  const bgColor =
    props.isCorrect !== undefined ? (props.isCorrect ? 'green.50' : 'red.50') : 'white';

  return (
    <Box
      as="label"
      cursor={readOnly ? 'default' : 'pointer'}
      {...rest}
      opacity={readOnly && !state.isChecked ? 0.8 : undefined}
    >
      <input
        {...getInputProps()}
        type="radio"
        autoFocus={autoFocus}
        data-testid={`Radio-${props.value}`}
        aria-labelledby={id}
        disabled={readOnly}
        aria-checked={state.isChecked}
      />
      <Box
        {...getRadioProps()}
        sx={{
          ...styles,
          _hover: !readOnly
            ? {
                ...('_hover' in styles && typeof styles._hover === 'object' ? styles._hover : {}),
                borderColor: 'border.active',
              }
            : {},
          ...(props.selectedOptions !== undefined
            ? { bg: props.selectedOptions?.includes(props.value) && bgColor }
            : {}),
        }}
        _focus={{ borderColor: 'brand.500' }}
        {...rest}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={4}>
          {state.isChecked ? (
            <Circle bg="brand.500" size="4">
              <Icon as={CheckIcon} boxSize="2.5" color="fg.inverted" />
            </Circle>
          ) : (
            <Circle borderWidth="2px" size="4" />
          )}
          <Box flex="1">{children}</Box>
        </Stack>
      </Box>
    </Box>
  );
}
